import { Fragment, useState, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Check, X, Film, Camera, FileText, Instagram, Play,
  AlertTriangle, TrendingUp, Share2,
  Search, ChevronDown, Loader2, AlertCircle, Youtube, Link2, ExternalLink,
} from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { inferMediaReferrerPolicy } from '@/utils/externalMediaReferrerPolicy';
import { motion } from 'motion/react';
import i18n from '@/i18n';
import {
  isDisplayableCollaborationThumbUrl,
  normalizeCollaborationThumbUrl,
} from '@/utils/mapCampaignCollaborationsToCollaborators';
import {
  getPlatformContentFilterOptions,
  postMatchesContentFilter,
  publicationPostTypeChipLabel,
  sortContentFilterOptionsAlphabetically,
} from '@/utils/contentReviewContentType';
import pubCard from './publicationPostCard.module.css';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from './ui/utils';

function isVideoPreviewUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const lower = url.trim().toLowerCase();
  const base = lower.split('?')[0] ?? '';
  if (/\.(mp4|mov|webm|m4v|m3u8)(\?|#|$)/i.test(base)) return true;
  if (lower.includes('/video/') || lower.includes('video.mp4')) return true;
  return false;
}

/* ────────────────────────── types ────────────────────────── */

export interface PostCompliance {
  brandTagged: boolean | null;
  hashtagUsed: boolean | null;
  productVisible: boolean | null;
  adDisclosure: boolean | null;
  linkIncluded: boolean | null;
}

export interface Post {
  type: 'Reel' | 'Story' | 'Carousel';
  thumbnail: string;
  /** Live post URL when API provides it (e.g. Instagram / TikGram link). */
  permalink?: string;
  /** TikTok Spark Ads ID when exposed by the client collaborations API. */
  spark_id?: string;
  /** Instagram / Meta ad ID when exposed by the client collaborations API. */
  instagram_ad_id?: string;
  caption: string;
  likes: number;
  comments: number;
  date: string;
  isVideo: boolean;
  reach: number;
  impressions: number;
  saves: number;
  shares: number;
  engagementRate: number;
  linkClicks: number;
  hashtags: string[];
  brandTags: string[];
  compliance: PostCompliance;
  /** Platform for this deliverable (defaults to collaborator platform in mapper). */
  platform?: 'Instagram' | 'TikTok' | 'YouTube';
  /** Same as API `product_id` — used to attach draft previews when handles differ. */
  product_id?: string;
  /** Collaboration product `status` from API (normalized uppercase in mapper). */
  product_status?: string;
  /** True only when the collaborations payload included at least one metric field for this product. */
  metrics_from_api?: boolean;
  /** When false (IGC), hide the metrics strip on the card. Undefined/true: show strip per metrics_from_api. */
  has_metrics_section?: boolean;
  /** Total interactions from API `metrics.engagement` when provided. */
  engagement_total?: number;
  /** True only when the collaborations payload included compliance booleans for this product. */
  compliance_from_api?: boolean;
  /** API `product_type` when present — used for platform-aware filters (optional). */
  product_type?: string;
  views?: number;
  avgWatchTime?: string;
  completionRate?: number;
  retention3s?: number;
  videoLength?: string;
  storyFrames?: number;
  tapsForward?: number;
  tapsBack?: number;
  exits?: number;
}

/** UTM / short links from GET collaborations (`trackers` or per-product tracker fields). */
export interface CollaborationTrackerRow {
  target_url: string;
  tracker_url: string;
  tracker_note?: string | null;
}

/** One row per API `beats[]` entry with a `beat_id` — opens that beat’s brief. */
export interface CollaboratorBriefLink {
  beatId: string;
  label: string;
}

export interface Collaborator {
  id: number;
  /** Discovery influencer id for `/creators/:id` when collaborations API provides it. */
  influencerId?: string;
  /** API collaboration id — matches drafts when backend sends `collaboration_id` on draft rows. */
  collaborationId?: string;
  name: string;
  handle: string;
  avatar: string;
  beat: string;
  followers: string;
  engRate: string;
  deadline: string;
  publication: string;
  status: 'Published' | 'Approved' | 'Pending';
  platform: 'Instagram' | 'TikTok' | 'YouTube';
  /** Shipping address or other delivery text from API (`delivery_address`). */
  address: string;
  posts: Post[];
  /** From API `product_delivery`; drives address / delivery UI. */
  product_delivery?: 'SHIPPING' | 'DIGITAL' | 'NONE' | string | null;
  /** True when API returned a non-empty `delivery_address`. */
  address_available?: boolean;
  /** When false, row shows — for compliance (API does not expose post-level compliance here). */
  compliance_available?: boolean;
  /** Tracking links for this collaboration (legacy “Links” column). */
  trackers?: CollaborationTrackerRow[];
  /** Per-beat brief links from collaborations `beats[]` (each `beat_id` is clickable). */
  briefBeatLinks?: CollaboratorBriefLink[];
}

/** Opens the campaign content player (modal) for a published deliverable. */
export type OpenPublishedPostContext = {
  collaborator: Collaborator;
  post: Post;
  postIndex: number;
  allPosts: Post[];
};

export interface ContentReviewSectionProps {
  /** Collaborators from API. When empty or undefined, empty state is shown (no mock). */
  collaborators?: Collaborator[];
  isLoading?: boolean;
  error?: string | null;
  /** When set, published post thumbnails open the shared content player. */
  onOpenPublishedPost?: (ctx: OpenPublishedPostContext) => void;
  /** When set, Brief column opens campaign beat brief (same overlay as Overview beats). */
  onOpenBeatBrief?: (beatId: string) => void;
  /** When set with `collaborator.influencerId`, creator avatar opens the Discovery profile sidebar (iframe). */
  onOpenCreatorProfile?: (influencerId: string) => void;
}

/* ────────────────────────── helpers ────────────────────────── */

const fmtK = (n: number) => {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
};

const getComplianceIssues = (posts: Post[]) =>
  posts.reduce((s, p) => {
    if (p.compliance_from_api !== true) return s;
    return s + Object.values(p.compliance).filter(v => v === false).length;
  }, 0);

function postComplianceIssueCount(post: Post): number {
  if (post.compliance_from_api !== true) return 0;
  return Object.values(post.compliance).filter(v => v === false).length;
}

const PUBLISHED_PRODUCT_STATUSES = new Set(['PUBLISHED', 'MEASURED']);
const DRAFT_PILL_STATUS = 'NO_DRAFT';
const CANCELED_PRODUCT_STATUS = 'CANCELED';
const PENDING_PRODUCT_STATUSES = new Set([
  'CM_REVIEW',
  'CM_REVIEW_FEEDBACK',
  'CLIENT_REVIEW',
  'DRAFT_APPROVED',
  'DRAFT_REJECTED',
]);

type ThumbnailStatusPill = 'draft' | 'pending' | 'canceled';

function thumbnailStatusPillFromProductStatus(statusUpper: string): ThumbnailStatusPill | null {
  if (!statusUpper) return null;
  if (statusUpper === DRAFT_PILL_STATUS) return 'draft';
  if (statusUpper === CANCELED_PRODUCT_STATUS) return 'canceled';
  if (PENDING_PRODUCT_STATUSES.has(statusUpper)) return 'pending';
  if (PUBLISHED_PRODUCT_STATUSES.has(statusUpper)) return null;
  return 'pending';
}

/** When the API omits per-product `status`, still show a pill (default PENDING — matches in-review / not live). */
function thumbnailStatusPillWithApiFallback(statusUpper: string): ThumbnailStatusPill {
  return thumbnailStatusPillFromProductStatus(statusUpper) ?? 'pending';
}

/** Same rule as the green "Published" subsection: live + legacy rows with compliance but no product status. */
function isPostPublishedGallerySection(post: Post): boolean {
  const productStatusUpper = (post.product_status ?? '').trim().toUpperCase();
  if (PUBLISHED_PRODUCT_STATUSES.has(productStatusUpper)) return true;
  const complianceFromApi = post.compliance_from_api === true;
  return !productStatusUpper && complianceFromApi;
}

/** Small platform mark on avatar — logos aligned with `SeeCampaignsPage` PlatformIcon (sm). */
function PlatformGlyph({ platform }: { platform: Collaborator['platform'] }) {
  const shell =
    'absolute -bottom-0.5 -right-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-white';
  const iconSm = 'h-2.5 w-2.5 shrink-0';
  if (platform === 'TikTok') {
    return (
      <div className={`${shell} bg-gray-100`} title={i18n.t('contentReview.platformNames.tiktok')}>
        <svg className={iconSm} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"
            fill="#101828"
          />
        </svg>
      </div>
    );
  }
  if (platform === 'YouTube') {
    return (
      <div className={`${shell} bg-red-50`} title={i18n.t('contentReview.platformNames.youtube')}>
        <Youtube className={`${iconSm} text-red-600`} aria-hidden />
      </div>
    );
  }
  return (
    <div className={`${shell} bg-pink-50`} title={i18n.t('contentReview.platformNames.instagram')}>
      <Instagram className={`${iconSm} text-pink-600`} aria-hidden />
    </div>
  );
}

/** Selected content-type filter key (legacy `Reel`/`Story`/`Carousel` when all platforms, or platform-specific keys). */
type ContentFilterKey = string;
/** Filter collaborator rows by primary channel (from API `platform` on each collaboration). */
type PlatformFilter = 'all' | Collaborator['platform'];

/** Desktop: wide metrics row (horizontal scroll on small legacy layout). Mobile: stack + wrap — no min-width overflow. */
const COLLAB_ROW_OUTER_CLASS =
  'box-border flex w-full shrink-0 gap-4 bg-white px-4 py-4 text-left transition-colors hover:bg-[#fafbfc] max-md:min-h-0 max-md:min-w-0 max-md:flex-col max-md:items-stretch md:min-h-[5rem] md:min-w-[80rem] md:flex-row md:items-center md:px-6 md:py-2';

/** Stat columns: label + value left-aligned within each cell; whole block sits on the right of the row. */
const STAT_CELL = 'min-w-0 max-w-[7.5rem] text-left';
const STAT_LABEL = 'text-[9px] tracking-[0.05em]';
const STAT_VALUE = 'mt-0.5 truncate text-[11px] font-semibold tabular-nums text-[#101828]';
const STAT_VALUE_TEXT = 'mt-0.5 truncate text-[11px] font-semibold text-[#101828]';
const STAT_VALUE_MUTED = 'mt-0.5 truncate text-[11px] font-semibold text-[#667085]';

/** Gray column label (reference: label above dark value). */
function ColLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`font-semibold uppercase leading-tight text-[#98a2b3] ${className ?? 'text-[10px] tracking-[0.06em]'}`.trim()}
    >
      {children}
    </div>
  );
}

function dashCell(v: string | undefined | null): string {
  const s = (v ?? '').trim();
  return s && s !== '–' ? s : '—';
}

/** Dot uses SVG `currentColor` = same computed color as the pill label (no bg-* utilities). */
function StatusPillLeadingDot({ pulse = false }: { pulse?: boolean }) {
  return (
    <svg
      className={`h-2 w-2 shrink-0 text-inherit ${pulse ? 'animate-pulse' : ''}`}
      width={8}
      height={8}
      viewBox="0 0 8 8"
      aria-hidden
    >
      <circle cx="4" cy="4" r="4" fill="currentColor" />
    </svg>
  );
}

/** Collaboration row status pills. */
function CollaborationRowStatusPill({ status, compact }: { status: Collaborator['status']; compact?: boolean }) {
  const shell = compact
    ? 'mt-0.5 inline-flex max-w-full min-w-0 items-center gap-1 rounded-full px-2.5 py-1 text-left text-[10px] font-bold uppercase leading-none tracking-[0.05em]'
    : 'mt-1 inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-left text-[11px] font-bold uppercase leading-none tracking-[0.05em]';

  if (status === 'Published') {
    return (
      <span className={`${shell} bg-emerald-100 text-emerald-800`} title={status}>
        <StatusPillLeadingDot />
        <span className="truncate whitespace-nowrap">{i18n.t('contentReview.statusPill.published')}</span>
      </span>
    );
  }

  if (status === 'Approved') {
    return (
      <span className={`${shell} bg-blue-100 text-blue-800`} title={status}>
        <StatusPillLeadingDot />
        <span className="truncate whitespace-nowrap">{i18n.t('contentReview.statusPill.approved')}</span>
      </span>
    );
  }

  return (
    <span className={`${shell} bg-amber-100 text-amber-800`} title={status}>
      <StatusPillLeadingDot pulse />
      <span className="truncate whitespace-nowrap">{i18n.t('contentReview.statusPill.pending')}</span>
    </span>
  );
}

/* ────────────────────────── component ────────────────────────── */

export function ContentReviewSection({
  collaborators: collaboratorsProp,
  isLoading,
  error,
  onOpenPublishedPost,
  onOpenBeatBrief,
  onOpenCreatorProfile,
}: ContentReviewSectionProps = {}) {
  const [contentFilter, setContentFilter] = useState<ContentFilterKey>('all');
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');
  const [creatorSearch, setCreatorSearch] = useState('');
  /** Tracking links popover (legacy “Links” column). */
  const [linksPopover, setLinksPopover] = useState<{ id: number; top: number; left: number } | null>(null);

  const collabDataToUse = collaboratorsProp ?? [];

  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label={i18n.t('contentReview.loadingPublicationsAria')}>
        <div className="bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] px-8 py-6">
          <h2 className="text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>{i18n.t('contentReview.publicationsPageTitle')}</h2>
        </div>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-[#be43ff] animate-spin" aria-hidden />
          <span className="sr-only">{i18n.t('contentReview.loadingPublicationsAria')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4" role="alert">
        <div className="bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] px-8 py-6">
          <h2 className="text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>{i18n.t('contentReview.publicationsPageTitle')}</h2>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-[20px] p-4 flex items-center gap-2 text-red-800 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (collabDataToUse.length === 0) {
    return (
      <div className="space-y-4" aria-label="No collaborators">
        <div className="bg-white rounded-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] px-8 py-6">
          <h2 className="text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>{i18n.t('contentReview.publicationsPageTitle')}</h2>
        </div>
        <div className="bg-white rounded-[20px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-12 text-center">
          <p className="text-small-medium text-[#667085]">No collaborators in this campaign yet. Accepted profiles will appear here.</p>
        </div>
      </div>
    );
  }

  /* ── aggregates (full campaign list, not filtered — Figma header summary) ── */
  const allPosts = collabDataToUse.flatMap(c => c.posts);
  const totalCreators = collabDataToUse.length;
  const totalPosts = allPosts.length;
  const totalComplianceIssues = getComplianceIssues(allPosts);
  const hasGlobalComplianceData = collabDataToUse.some(c => c.compliance_available === true);

  const getPostsForMenuPlatform = (menuPlatformKey: PlatformFilter) =>
    collabDataToUse
      .filter((c) => menuPlatformKey === 'all' || c.platform === menuPlatformKey)
      .flatMap((c) => {
        let posts = c.posts;
        if (menuPlatformKey !== 'all') {
          posts = posts.filter((p) => (p.platform ?? c.platform) === menuPlatformKey);
        }
        return posts;
      });

  const isContentTypeAvailableForMenu = (filterKey: string, menuPlatformKey: PlatformFilter): boolean => {
    if (filterKey === 'all') return true;
    return getPostsForMenuPlatform(menuPlatformKey).some((p) =>
      postMatchesContentFilter(p, menuPlatformKey, filterKey),
    );
  };

  /* ── filtering ── */
  const filtered = collabDataToUse
    .filter((c) => platformFilter === 'all' || c.platform === platformFilter)
    .map(c => {
      let posts = c.posts;
      if (platformFilter !== 'all') {
        posts = posts.filter((p) => (p.platform ?? c.platform) === platformFilter);
      }
      if (contentFilter !== 'all') {
        posts = posts.filter((p) => postMatchesContentFilter(p, platformFilter, contentFilter));
      }
      return { ...c, posts };
    })
    .filter(c => {
      if (contentFilter === 'all') return true;
      return c.posts.length > 0;
    })
    .filter(c => {
      if (creatorSearch) {
        const q = creatorSearch.toLowerCase();
        return c.name.toLowerCase().includes(q) || c.handle.toLowerCase().includes(q);
      }
      return true;
    });

  return (
    <div className="space-y-4">

      {/* ═══════════════ 1. PUBLICATIONS TITLE CARD (Figma: title + summary + compliance badge) ═══════════════ */}
      <div className="flex flex-col gap-4 rounded-[24px] bg-white px-4 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] md:flex-row md:items-start md:justify-between md:px-8 md:py-6">
        <div>
          <h2 className="text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>{i18n.t('contentReview.publicationsPageTitle')}</h2>
          <p className="text-small text-[#667085] mt-1.5" style={{ fontWeight: 500 }}>
            {totalCreators} {totalCreators === 1 ? 'creator' : 'creators'} · {totalPosts} {totalPosts === 1 ? 'post' : 'posts'}
          </p>
        </div>
        {hasGlobalComplianceData && totalComplianceIssues > 0 && (
          <div
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-amber-50 text-amber-900 border border-amber-200/80 shrink-0"
            role="status"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" aria-hidden />
            <span className="text-small" style={{ fontWeight: 700 }}>
              {totalComplianceIssues} compliance {totalComplianceIssues === 1 ? 'issue' : 'issues'}
            </span>
          </div>
        )}
      </div>

      {/* ═══════════════ 2. FILTERS (per-platform content menu + search) ═══════════════ */}
      <div className="flex max-w-full flex-col gap-3 overflow-x-hidden rounded-[20px] bg-white px-4 py-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] md:px-7 md:py-4">
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-3">
          <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-[#667085]">
            {i18n.t('contentReview.filters.platformLabel')}
          </span>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {(
              [
                { key: 'all' as PlatformFilter, label: i18n.t('contentReview.filters.allPlatforms'), icon: null },
                {
                  key: 'Instagram' as PlatformFilter,
                  label: i18n.t('contentReview.platformNames.instagram'),
                  icon: <Instagram className="w-3 h-3 text-pink-600" aria-hidden />,
                },
                {
                  key: 'TikTok' as PlatformFilter,
                  label: i18n.t('contentReview.platformNames.tiktok'),
                  icon: (
                    <svg className="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"
                        fill="currentColor"
                        className="text-[#101828]"
                      />
                    </svg>
                  ),
                },
                {
                  key: 'YouTube' as PlatformFilter,
                  label: i18n.t('contentReview.platformNames.youtube'),
                  icon: <Youtube className="h-3 w-3 text-red-600" aria-hidden />,
                },
              ] as const
            ).map((f) => {
              const menuOptions = sortContentFilterOptionsAlphabetically(getPlatformContentFilterOptions(f.key));
              return (
                <DropdownMenu key={f.key}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-xl px-3 py-1.5 transition-all',
                        platformFilter === f.key
                          ? 'bg-[#101828] text-white shadow-[0_2px_6px_rgba(0,0,0,0.1)]'
                          : 'text-[#667085] hover:bg-[#f8f9fa]',
                      )}
                      aria-label={f.label}
                      aria-haspopup="menu"
                    >
                      {f.icon}
                      <span className="text-small truncate" style={{ fontWeight: 600 }}>
                        {f.label}
                      </span>
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="z-[200] max-h-[min(22rem,var(--radix-dropdown-menu-content-available-height))] rounded-xl border border-gray-200 bg-white p-1 text-[#101828] shadow-lg"
                  >
                    {menuOptions.map((opt) => {
                      const selected = platformFilter === f.key && contentFilter === opt.key;
                      const disabled = !isContentTypeAvailableForMenu(opt.key, f.key);
                      return (
                        <DropdownMenuItem
                          key={opt.key}
                          disabled={disabled}
                          onSelect={() => {
                            setPlatformFilter(f.key);
                            setContentFilter(opt.key);
                          }}
                          className="cursor-pointer rounded-lg text-[#101828] focus:bg-[#f3f4f6] focus:text-[#101828] data-[highlighted]:bg-[#f3f4f6] data-[highlighted]:text-[#101828]"
                        >
                          {selected ? (
                            <Check className="h-3.5 w-3.5 shrink-0 text-[#101828]" aria-hidden />
                          ) : (
                            <span className="inline-block w-3.5 shrink-0" aria-hidden />
                          )}
                          <span className={selected ? 'font-semibold text-[#101828]' : 'text-[#101828]'}>{opt.label}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </div>

          {/* Creator search */}
          <div className="relative w-full min-w-0 md:ml-auto md:max-w-[13rem] md:flex-none">
            <Search className="absolute left-3 top-1/2 w-3.5 -translate-y-1/2 text-[#98a2b3]" />
            <input
              type="text"
              placeholder={i18n.t('contentReview.filters.searchPlaceholder')}
              aria-label={i18n.t('contentReview.filters.searchAria')}
              value={creatorSearch}
              onChange={(e) => setCreatorSearch(e.target.value)}
              className="w-full min-w-0 rounded-xl border border-gray-200 bg-[#f8f9fa] py-1.5 pl-9 pr-3 text-small text-[#101828] placeholder:text-[#98a2b3] transition-all focus:border-[#be43ff]/40 focus:outline-none focus:ring-1 focus:ring-[#be43ff]/30 md:w-52"
            />
            {creatorSearch && (
              <button
                type="button"
                onClick={() => setCreatorSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#98a2b3] hover:text-[#667085]"
                aria-label={i18n.t('contentReview.filters.clearSearchAria')}
              >
                <X className="w-3 h-3" aria-hidden />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════ 3. CREATOR CARDS (header row + publication gallery always visible) ═══════════════ */}
      <div className="space-y-8">
        {filtered.length === 0 && (
          <div className="rounded-[20px] bg-white p-8 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)] md:p-12">
            <Search className="w-8 h-8 text-[#d0d5dd] mx-auto mb-3" />
            <p className="text-small-medium text-[#667085]">
              {collabDataToUse.length > 0 && collabDataToUse.every(c => c.posts.length === 0)
                ? i18n.t('contentReview.emptyFiltered.noCollaboratorContentYet')
                : i18n.t('contentReview.emptyFiltered.noCreatorsMatchFilters')}
            </p>
          </div>
        )}

        {filtered.length > 0 &&
          filtered.map(c => {
            const trackers = c.trackers ?? [];
            const hasLinks = trackers.length > 0;

            return (
              <Fragment key={c.id}>
              <div
                className={`group/creator relative rounded-[20px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-[box-shadow,transform] duration-300 ease-out hover:-translate-y-px hover:shadow-[0_2px_8px_rgba(0,0,0,0.045),0_1px_3px_rgba(0,0,0,0.04)] ${
                  linksPopover?.id === c.id ? 'z-30' : 'z-0 hover:z-20'
                }`}
              >
                {/* Inner: overflow only — shadow on parent matches title/filter pills cards (no border) */}
                <div className="overflow-hidden rounded-[20px] bg-white">
                {/* z-20 keeps header row + links popover above the gallery pane below */}
                <div className="relative z-20 max-md:overflow-visible overflow-x-auto rounded-t-[20px] [scrollbar-width:thin]">
                  <div className={COLLAB_ROW_OUTER_CLASS}>
                    {/* Creator — stays left */}
                    <div className="flex min-h-0 w-full min-w-0 shrink-0 items-center gap-3 max-md:max-w-none md:max-w-[min(42%,18rem)]">
                      {c.influencerId && onOpenCreatorProfile ? (
                        <button
                          type="button"
                          className="relative h-10 w-10 shrink-0 cursor-pointer rounded-full p-0 text-left ring-offset-2 ring-offset-white transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff]/50"
                          onClick={(e) => {
                            e.stopPropagation();
                            const id = c.influencerId;
                            if (id) onOpenCreatorProfile(id);
                          }}
                          aria-label={i18n.t('contentReview.openCreatorProfileAria', { name: c.name })}
                        >
                          <ImageWithFallback
                            src={c.avatar}
                            alt=""
                            className="h-10 w-10 min-h-[2.5rem] min-w-[2.5rem] rounded-full border-2 border-[#e8eaed] object-cover"
                          />
                          <PlatformGlyph platform={c.platform} />
                        </button>
                      ) : (
                        <div className="relative h-10 w-10 shrink-0">
                          <ImageWithFallback
                            src={c.avatar}
                            alt=""
                            className="h-10 w-10 min-h-[2.5rem] min-w-[2.5rem] rounded-full border-2 border-[#e8eaed] object-cover"
                          />
                          <PlatformGlyph platform={c.platform} />
                        </div>
                      )}
                      <div className="min-w-0">
                        <ColLabel>{i18n.t('contentReview.columns.creator')}</ColLabel>
                        <div className="mt-1 truncate text-[14px] font-semibold leading-tight text-[#101828]" title={c.name}>
                          {c.name}
                        </div>
                        <div className="mt-0.5 truncate text-[13px] leading-relaxed text-[#667085]" title={`@${c.handle}`}>
                          @{c.handle}
                        </div>
                      </div>
                    </div>

                    {/* Metrics — grouped and pushed to the right; each column title + value left-aligned */}
                    <div className="flex min-w-0 flex-1 justify-end max-md:w-full max-md:justify-start">
                      <div className="flex min-w-0 max-w-full flex-none flex-wrap items-start justify-end gap-x-3 gap-y-2 max-md:justify-start lg:gap-x-4">
                      {/* 2 — Followers */}
                      <div className={STAT_CELL} title={dashCell(c.followers)}>
                        <ColLabel className={STAT_LABEL}>{i18n.t('contentReview.columns.followers')}</ColLabel>
                        <div className={STAT_VALUE}>{dashCell(c.followers)}</div>
                      </div>

                      {/* 3 — Engagement rate */}
                      <div className={STAT_CELL} title={dashCell(c.engRate)}>
                        <ColLabel className={STAT_LABEL}>{i18n.t('contentReview.columns.engagement')}</ColLabel>
                        <div className={STAT_VALUE}>{dashCell(c.engRate)}</div>
                      </div>

                      {/* 4 — Deadline */}
                      <div className={STAT_CELL} title={dashCell(c.deadline)}>
                        <ColLabel className={STAT_LABEL}>{i18n.t('contentReview.columns.deadline')}</ColLabel>
                        <div className={STAT_VALUE}>{dashCell(c.deadline)}</div>
                      </div>

                      {/* 5 — Publication */}
                      <div className={STAT_CELL} title={dashCell(c.publication)}>
                        <ColLabel className={STAT_LABEL}>{i18n.t('contentReview.columns.publication')}</ColLabel>
                        <div className={STAT_VALUE}>{dashCell(c.publication)}</div>
                      </div>

                      {/* 5b — Links (trackers / UTM — legacy web “SHOW” under Links) */}
                      <div className={`relative z-10 ${STAT_CELL}`}>
                        <ColLabel className={STAT_LABEL}>{i18n.t('contentReview.columns.links')}</ColLabel>
                        {hasLinks ? (
                          <div className="mt-0.5 min-w-0">
                            <button
                              type="button"
                              className="block max-w-full truncate whitespace-nowrap text-left text-[11px] font-semibold text-[#101828] underline decoration-[#d0d5dd] decoration-1 underline-offset-2 hover:text-[#be43ff] hover:decoration-[#be43ff]"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (linksPopover?.id === c.id) {
                                  setLinksPopover(null);
                                  return;
                                }
                                const r = e.currentTarget.getBoundingClientRect();
                                setLinksPopover({ id: c.id, top: r.bottom + 8, left: r.left });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (linksPopover?.id === c.id) {
                                    setLinksPopover(null);
                                    return;
                                  }
                                  const r = e.currentTarget.getBoundingClientRect();
                                  setLinksPopover({ id: c.id, top: r.bottom + 8, left: r.left });
                                }
                              }}
                              title={i18n.t('contentReview.links.viewLinks')}
                            >
                              {i18n.t('contentReview.links.viewLinks')}
                            </button>
                          </div>
                        ) : (
                          <div className={STAT_VALUE_MUTED}>{i18n.t('contentReview.links.noLinks')}</div>
                        )}
                      </div>

                      {/* 6 — Brief */}
                      <div className={STAT_CELL} title={c.beat}>
                        <ColLabel className={STAT_LABEL}>{i18n.t('contentReview.columns.brief')}</ColLabel>
                        {c.briefBeatLinks?.length && onOpenBeatBrief ? (
                          <div className="mt-0.5 min-w-0 flex flex-wrap items-baseline gap-y-0.5">
                            {c.briefBeatLinks.map((b, briefIdx) => (
                              <Fragment key={b.beatId}>
                                {briefIdx > 0 ? (
                                  <span className="text-[11px] text-[#667085] shrink-0 pr-1" aria-hidden>
                                    {', '}
                                  </span>
                                ) : null}
                                <button
                                  type="button"
                                  className="max-w-full min-w-0 truncate text-left text-[11px] font-semibold text-[#101828] underline decoration-[#d0d5dd] decoration-1 underline-offset-2 hover:text-[#be43ff] hover:decoration-[#be43ff]"
                                  title={b.label}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLinksPopover(null);
                                    onOpenBeatBrief(b.beatId);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setLinksPopover(null);
                                      onOpenBeatBrief(b.beatId);
                                    }
                                  }}
                                  aria-label={i18n.t('contentReview.brief.openBeatBriefAria', { beat: b.label })}
                                >
                                  {b.label}
                                </button>
                              </Fragment>
                            ))}
                          </div>
                        ) : (
                          <div className={STAT_VALUE_TEXT}>{dashCell(c.beat)}</div>
                        )}
                      </div>

                      {/* 7 — Status */}
                      <div className="flex min-w-0 shrink-0 flex-col items-start text-left">
                        <ColLabel className={STAT_LABEL}>{i18n.t('contentReview.columns.status')}</ColLabel>
                        <CollaborationRowStatusPill status={c.status} compact />
                      </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative z-0 min-w-0 rounded-b-[20px] border-t border-solid border-gray-100 bg-white py-3 md:py-4">
                  <CreatorPublicationGallery
                    collaborator={c}
                    posts={c.posts}
                    defaultPlatform={c.platform}
                    onOpenPublishedPost={onOpenPublishedPost}
                  />
                </div>
                </div>
              </div>

              {linksPopover?.id === c.id &&
              hasLinks &&
              typeof document !== 'undefined'
                ? createPortal(
                    <>
                      <div
                        className="fixed inset-0 z-[80] bg-transparent"
                        aria-hidden
                        onClick={() => setLinksPopover(null)}
                      />
                      <motion.div
                        role="dialog"
                        aria-label={i18n.t('contentReview.links.title')}
                        initial={{ opacity: 0, y: 6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed z-[100] w-[min(32rem,calc(100vw-1.5rem))] max-h-[min(28rem,70vh)] overflow-y-auto rounded-2xl border border-[#e8eaed] bg-white p-5 text-left shadow-[0_20px_50px_rgba(15,23,42,0.16),0_8px_24px_rgba(15,23,42,0.08)]"
                        style={{
                          top: linksPopover.top,
                          left: (() => {
                            const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
                            const maxLeft = vw - Math.min(512, vw - 24) - 12;
                            return Math.max(12, Math.min(linksPopover.left, maxLeft));
                          })(),
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex gap-4">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-50">
                            <Link2 className="h-5 w-5 text-sky-600" aria-hidden />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#667085]">
                              {i18n.t('contentReview.links.title')}
                            </p>
                            <ul className="mt-3 space-y-4">
                              {trackers.map((tr, idx) => {
                                const same =
                                  tr.target_url.trim().toLowerCase() === tr.tracker_url.trim().toLowerCase();
                                return (
                                  <li
                                    key={`${tr.tracker_url}-${idx}`}
                                    className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0"
                                  >
                                    {tr.tracker_note ? (
                                      <p className="mb-2 text-[13px] font-medium leading-snug text-[#344054] whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                                        {tr.tracker_note}
                                      </p>
                                    ) : null}
                                    <div className="space-y-2">
                                      <div>
                                        <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-[#98a2b3]">
                                          {same
                                            ? i18n.t('contentReview.links.linkLabel')
                                            : i18n.t('contentReview.links.shortLink')}
                                        </span>
                                        <a
                                          href={tr.tracker_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="mt-0.5 flex items-start gap-1.5 break-all text-[13px] font-semibold text-[#be43ff] hover:underline"
                                        >
                                          <span className="min-w-0 flex-1">{tr.tracker_url}</span>
                                          <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                                        </a>
                                      </div>
                                      {!same ? (
                                        <div>
                                          <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-[#98a2b3]">
                                            {i18n.t('contentReview.links.destination')}
                                          </span>
                                          <a
                                            href={tr.target_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-0.5 flex items-start gap-1.5 break-all text-[13px] font-semibold text-[#2563eb] hover:underline"
                                          >
                                            <span className="min-w-0 flex-1">{tr.target_url}</span>
                                            <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                                          </a>
                                        </div>
                                      ) : null}
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      </motion.div>
                    </>,
                    document.body,
                  )
                : null}
              </Fragment>
            );
          })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  Creator publication gallery (dashboard — not a compact rail)   */
/* ═══════════════════════════════════════════════════════════════ */

/**
 * Section title + status dot.
 * Uses a div+role=heading (not h3) so global `@layer base` h3 + body `text-foreground` in dark mode
 * cannot wash out the label on white cards. Colors are inline so they never inherit lavender on white.
 */
function PublicationGallerySectionLabel({ children }: { children: ReactNode }) {
  const fill = '#10b981';
  return (
    <div className="mb-5 flex min-h-0 min-w-0 items-center gap-2.5 md:mb-10">
      <span
        aria-hidden
        style={{
          boxSizing: 'border-box',
          width: 10,
          height: 10,
          flexShrink: 0,
          borderRadius: 9999,
          backgroundColor: fill,
          border: '2px solid rgba(255, 255, 255, 0.95)',
          display: 'block',
        }}
      />
      <div
        role="heading"
        aria-level={3}
        style={{
          margin: 0,
          minWidth: 0,
          color: '#344054',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          lineHeight: 1,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function CreatorPublicationGallery({
  collaborator,
  posts,
  defaultPlatform,
  onOpenPublishedPost,
}: {
  collaborator: Collaborator;
  posts: Post[];
  defaultPlatform: Collaborator['platform'];
  onOpenPublishedPost?: (ctx: OpenPublishedPostContext) => void;
}) {
  const indexed = posts.map((post, index) => ({ post, index }));
  const publishedItems = indexed.filter(({ post }) => isPostPublishedGallerySection(post));

  return (
    <div className="px-3 pb-8 pt-6 md:px-8 md:pb-16 md:pt-11 lg:px-10">
      {posts.length === 0 ? (
        <p className="max-w-xl text-[13px] leading-relaxed text-[#98a2b3]" role="status">
          {i18n.t('contentReview.noDeliverablesForCreator')}
        </p>
      ) : publishedItems.length === 0 ? (
        <p className="max-w-xl text-[13px] leading-relaxed text-[#98a2b3]" role="status">
          {i18n.t('contentReview.noPublishedPostsForCreator')}
        </p>
      ) : (
        <div>
          <PublicationGallerySectionLabel>
            {i18n.t('contentReview.publishedHeading', { count: publishedItems.length })}
          </PublicationGallerySectionLabel>
          <div className={pubCard.publishedGrid}>
            {publishedItems.map(({ post, index }) => (
              <PublicationPostCard
                key={`pub-${post.product_id ?? index}-${post.date}-${post.type}`}
                post={post}
                platform={post.platform ?? defaultPlatform}
                postIndex={index}
                allPosts={posts}
                collaborator={collaborator}
                onOpenPublishedPost={onOpenPublishedPost}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Post preview card aligned with publications reference: ~4:5 media, metrics on image, caption below. */
function PublicationPostCard({
  post,
  platform,
  postIndex,
  allPosts,
  collaborator,
  onOpenPublishedPost,
}: {
  post: Post;
  platform: Collaborator['platform'];
  postIndex: number;
  allPosts: Post[];
  collaborator: Collaborator;
  onOpenPublishedPost?: (ctx: OpenPublishedPostContext) => void;
}) {
  const showMetricsSection = post.has_metrics_section !== false;
  const metricsFromApi = post.metrics_from_api === true;
  const complianceFromApi = post.compliance_from_api === true;
  const complianceIssues = postComplianceIssueCount(post);
  const showViews =
    metricsFromApi && post.type === 'Reel' && post.views != null && post.views > 0;

  const typeIconSm =
    post.type === 'Reel' ? (
      <Film className="h-2.5 w-2.5 shrink-0 text-white" strokeWidth={2.5} />
    ) : post.type === 'Story' ? (
      <Camera className="h-2.5 w-2.5 shrink-0 text-white" strokeWidth={2.5} />
    ) : (
      <FileText className="h-2.5 w-2.5 shrink-0 text-white" strokeWidth={2.5} />
    );

  const thumb = normalizeCollaborationThumbUrl(post.thumbnail ?? '');
  const hasDisplayableThumb = isDisplayableCollaborationThumbUrl(thumb);
  const showVideoPreview = hasDisplayableThumb && (post.isVideo === true || isVideoPreviewUrl(thumb));
  const setVideoReferrerPolicy = useCallback((el: HTMLVideoElement | null) => {
    if (!el) return;
    const p = inferMediaReferrerPolicy(thumb, undefined);
    if (p) el.setAttribute('referrerpolicy', p);
    else el.removeAttribute('referrerpolicy');
  }, [thumb]);

  const complianceTitle =
    complianceIssues > 0
      ? complianceIssues === 1
        ? i18n.t('contentReview.complianceSingleIssueTitle')
        : i18n.t('contentReview.complianceMultiIssuesTitle', { count: complianceIssues })
      : i18n.t('contentReview.complianceVerifiedTitle');

  const productStatusUpper = (post.product_status ?? '').trim().toUpperCase();
  const showPublishedComplianceCorner = isPostPublishedGallerySection(post);
  const statusPill = !showPublishedComplianceCorner
    ? thumbnailStatusPillWithApiFallback(productStatusUpper)
    : null;
  const pillLabel =
    statusPill === 'draft'
      ? i18n.t('contentReview.thumbnailBadge.draft')
      : statusPill === 'canceled'
        ? i18n.t('contentReview.thumbnailBadge.canceled')
        : statusPill === 'pending'
          ? i18n.t('contentReview.thumbnailBadge.pending')
          : '';
  const pillClass =
    statusPill === 'draft'
      ? pubCard.statusPillDraft
      : statusPill === 'canceled'
        ? pubCard.statusPillCanceled
        : statusPill === 'pending'
          ? pubCard.statusPillPending
          : '';
  const pillTitle =
    statusPill === 'draft'
      ? i18n.t('contentReview.thumbnailBadge.draftTitle')
      : statusPill === 'canceled'
        ? i18n.t('contentReview.thumbnailBadge.canceledTitle')
        : statusPill === 'pending'
          ? i18n.t('contentReview.thumbnailBadge.pendingTitle')
          : '';

  const canOpenPlayer = Boolean(onOpenPublishedPost);
  const openLarger = () => {
    if (onOpenPublishedPost) {
      onOpenPublishedPost({ collaborator, post, postIndex, allPosts });
    }
  };

  return (
    <article className={pubCard.card}>
      <div
        className={`${pubCard.mediaShell} ${canOpenPlayer ? pubCard.mediaShellInteractive : ''}`}
        role={canOpenPlayer ? 'button' : undefined}
        tabIndex={canOpenPlayer ? 0 : undefined}
        onClick={
          canOpenPlayer
            ? (e) => {
                e.stopPropagation();
                openLarger();
              }
            : undefined
        }
        onKeyDown={
          canOpenPlayer
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  openLarger();
                }
              }
            : undefined
        }
        aria-label={canOpenPlayer ? i18n.t('contentReview.openContentPanelAria') : undefined}
      >
        <div className={pubCard.aspectLock}>
          <div className={pubCard.stack}>
            <div className={pubCard.mediaLayer}>
              {hasDisplayableThumb ? (
                showVideoPreview ? (
                  <video
                    ref={setVideoReferrerPolicy}
                    src={thumb}
                    muted
                    playsInline
                    preload="metadata"
                    className={pubCard.mediaFill}
                    aria-label={i18n.t('contentReview.videoPreviewAria')}
                  />
                ) : (
                  <ImageWithFallback src={thumb} alt="" className={pubCard.mediaFill} />
                )
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-[#d0d5dd] px-4 text-center">
                  <span className="max-w-[18rem] text-[12px] font-medium leading-5 text-[#475467]">
                    {i18n.t('contentReview.noPreviewThumbnail')}
                  </span>
                </div>
              )}
            </div>

            {hasDisplayableThumb ? <div className={pubCard.toneOverlay} aria-hidden /> : null}

            <div className={pubCard.chipWrap}>
              <span className={pubCard.chip} title={platform}>
                {typeIconSm}
                <span className={pubCard.chipLabel}>{publicationPostTypeChipLabel(post, platform)}</span>
              </span>
            </div>

            {showPublishedComplianceCorner ? (
              <div
                className={pubCard.complianceWrap}
                title={complianceTitle}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="presentation"
              >
                {complianceFromApi && complianceIssues > 0 ? (
                  <div className={`${pubCard.complianceBadge} ${pubCard.complianceBadgeWarning}`}>
                    <AlertTriangle className="h-4 w-4 text-white" aria-hidden strokeWidth={2.5} />
                  </div>
                ) : (
                  <div className={`${pubCard.complianceBadge} ${pubCard.complianceBadgeOk}`}>
                    <Check className="h-4 w-4 text-white" strokeWidth={3} aria-hidden />
                  </div>
                )}
              </div>
            ) : statusPill ? (
              <div
                className={pubCard.statusPillWrap}
                title={pillTitle}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="presentation"
              >
                <span className={`${pubCard.statusPill} ${pillClass}`}>{pillLabel}</span>
              </div>
            ) : null}

            {showMetricsSection ? (
              <div className={pubCard.metricsRow}>
                {metricsFromApi ? (
                  <div className={pubCard.metricsInner}>
                    <div className={pubCard.metricLeft}>
                      <TrendingUp className="h-3 w-3 shrink-0 text-white" strokeWidth={2.5} aria-hidden />
                      <span className={`${pubCard.metricValue} truncate`}>
                        {post.engagement_total != null && Number.isFinite(post.engagement_total)
                          ? fmtK(post.engagement_total)
                          : `${post.engagementRate.toFixed(2)}%`}
                      </span>
                    </div>
                    <div className={pubCard.metricRight}>
                      {showViews ? (
                        <Play className="h-3 w-3 shrink-0 fill-white text-white" aria-hidden />
                      ) : (
                        <Share2 className="h-3 w-3 shrink-0 text-white" strokeWidth={2.5} aria-hidden />
                      )}
                      <span className={pubCard.metricValue}>
                        {showViews ? fmtK(post.views as number) : fmtK(post.shares)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className={pubCard.metricPlaceholder}>—</p>
                )}
              </div>
            ) : null}

            {canOpenPlayer && hasDisplayableThumb && !post.isVideo ? (
              <div className={`${pubCard.playHover} ${pubCard.playHoverImage}`}>
                <div className="content-media-control content-media-control--nav pointer-events-none" aria-hidden>
                  <Play className="ml-1 h-5 w-5 text-white" fill="white" strokeWidth={0} />
                </div>
              </div>
            ) : null}
            {hasDisplayableThumb && post.isVideo ? (
              <div className={`${pubCard.playHover} ${pubCard.playHoverVideo}`}>
                <div className="content-media-control content-media-control--play pointer-events-none" aria-hidden>
                  <Play className="ml-1 h-6 w-6 text-white" fill="white" strokeWidth={0} />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {post.caption ? (
        <p className={pubCard.caption} title={post.caption}>
          {post.caption}
        </p>
      ) : (
        <p className={`${pubCard.caption} ${pubCard.captionPlaceholder}`}>&nbsp;</p>
      )}
    </article>
  );
}